import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '#environments';
import {
    BulkSubmitMonthlyEntries,
    BulkUploadMonthlyEntriesBatchResponse,
    GetMonthlyEntryBatchBulkUploadJobStatusResponse,
    GetMonthlyEntriesMasterListParams,
    MonthlyEntry,
    MonthlyEntryMasterListItem,
    PaginatedResult,
    SubmitMonthlyEntry,
    UpdateMonthlyEntry,
} from '#models';

// Must match the multi-remediation batch bulk-upload template's wide/pivoted columns exactly
// (see MonthlyEntryBatchCsvRowDto in the API), so an export can be edited and re-uploaded as-is.
const MONTHLY_ENTRIES_CSV_HEADER = [
    'RemediationId',
    'Year',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

@Injectable({ providedIn: 'root' })
export class MonthlyEntriesService {
    private readonly _http = inject(HttpClient);
    private readonly _base = `${environment.apiBaseUrl}/v1/remediations`;

    getByRemediation(remediationId: string): Observable<MonthlyEntry[]> {
        return this._http.get<MonthlyEntry[]>(`${this._base}/${remediationId}/monthly-entries`);
    }

    submit(remediationId: string, dto: SubmitMonthlyEntry): Observable<MonthlyEntry> {
        return this._http.post<MonthlyEntry>(`${this._base}/${remediationId}/monthly-entries`, dto);
    }

    submitBulk(remediationId: string, dto: BulkSubmitMonthlyEntries): Observable<MonthlyEntry[]> {
        return this._http.post<MonthlyEntry[]>(`${this._base}/${remediationId}/monthly-entries/bulk`, dto);
    }

    update(remediationId: string, monthlyEntryId: string, dto: UpdateMonthlyEntry): Observable<MonthlyEntry> {
        return this._http.put<MonthlyEntry>(`${this._base}/${remediationId}/monthly-entries/${monthlyEntryId}`, dto);
    }

    // ── Master "Monthly Entries" tab (cross-remediation) ──────────────────────────

    private readonly _masterBase = `${environment.apiBaseUrl}/v1/monthly-entries`;

    private _buildMasterParams(params: Partial<GetMonthlyEntriesMasterListParams>): HttpParams {
        let httpParams = new HttpParams();
        if (params.search) httpParams = httpParams.set('search', params.search);
        if (params.year) httpParams = httpParams.set('year', params.year);
        if (params.status) httpParams = httpParams.set('status', params.status);
        if (params.vendorId) httpParams = httpParams.set('vendorId', params.vendorId);
        if (params.page !== undefined) httpParams = httpParams.set('page', params.page);
        if (params.pageSize !== undefined) httpParams = httpParams.set('pageSize', params.pageSize);
        return httpParams;
    }

    /** Returns a paginated list of Monthly Entries across ALL remediations, for the master "Monthly Entries" tab. */
    getMasterList(params: GetMonthlyEntriesMasterListParams): Observable<PaginatedResult<MonthlyEntryMasterListItem>> {
        return this._http.get<PaginatedResult<MonthlyEntryMasterListItem>>(this._masterBase, {
            params: this._buildMasterParams(params),
        });
    }

    /**
     * Exports Monthly Entries matching the given filters as CSV. Fetches every matching
     * RemediationId+Year group in one call (page/pageSize omitted) via the same list endpoint
     * used by the grid, then builds the CSV client-side — mirrors the Remediation export pattern
     * rather than a dedicated server-side export endpoint.
     */
    exportMasterCsv(params: Partial<GetMonthlyEntriesMasterListParams>): Observable<Blob> {
        return this.getMasterList({ ...params, page: undefined, pageSize: undefined }).pipe(
            map(result => this._buildMasterCsvBlob(result.items)),
        );
    }

    /** Uploads a CSV/XLSX file for async multi-remediation batch bulk upload processing. Returns 202 Accepted with a job ID. */
    uploadBatchCsv(file: File): Observable<BulkUploadMonthlyEntriesBatchResponse> {
        const formData = new FormData();
        formData.append('csvFile', file, file.name);
        return this._http.post<BulkUploadMonthlyEntriesBatchResponse>(`${this._masterBase}/bulk-upload`, formData);
    }

    /** Gets the current status of a Monthly Entries batch bulk upload job. */
    getBatchBulkUploadStatus(jobId: string): Observable<GetMonthlyEntryBatchBulkUploadJobStatusResponse> {
        return this._http.get<GetMonthlyEntryBatchBulkUploadJobStatusResponse>(
            `${this._masterBase}/bulk-upload-jobs/${jobId}`,
        );
    }

    /** Downloads the error report for a Monthly Entries batch bulk upload job by streaming it through the API. */
    downloadBatchErrorReport(jobId: string): Observable<Blob> {
        return this._http.get(`${this._masterBase}/bulk-upload-jobs/${jobId}/error-report`, {
            responseType: 'blob',
        });
    }

    /** Triggers a browser download of the given blob, saved under the given filename. */
    downloadBlob(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    /** Wide format: one row per RemediationId+Year, one column per calendar month — matching the multi-remediation bulk-upload template exactly. */
    private _buildMasterCsvBlob(items: MonthlyEntryMasterListItem[]): Blob {
        const groups = new Map<string, { remediationId: string; year: number; amounts: number[] }>();

        for (const item of items) {
            const key = `${item.remediationId}|${item.year}`;
            let group = groups.get(key);
            if (!group) {
                group = { remediationId: item.remediationId, year: item.year, amounts: Array(12).fill(0) };
                groups.set(key, group);
            }
            group.amounts[item.month - 1] = item.amount;
        }

        const rows = [...groups.values()]
            .sort((a, b) => a.remediationId.localeCompare(b.remediationId) || b.year - a.year)
            .map(g =>
                [g.remediationId, String(g.year), ...g.amounts.map(String)]
                    .map(value => this._escapeCsvValue(value))
                    .join(','),
            );

        const csvContent = [MONTHLY_ENTRIES_CSV_HEADER.join(','), ...rows].join('\n');
        return new Blob([csvContent], { type: 'text/csv' });
    }

    private _escapeCsvValue(value: string): string {
        if (/[",\n]/.test(value)) {
            return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
    }
}

